// Frequency gate for the Lead Agent handoff reason modal.
//
// RET-1069 V2: per-entry-point time-window caps (Fanny's spec).
//
//   tomar_control       → at most once per 4-hour window (per user)
//   apagar_bot_chat     → at most once per 30-minute window (per user)
//   desactivar_workspace → every time, no cap
//
// Why per-entry-point instead of a workspace-wide policy: each surface
// has a different burst pattern. Operators take 20 chats in a morning
// (tomar_control is high-frequency, 4h cap keeps it from spamming during
// a shift but still catches morning + afternoon distinct contexts).
// Apagar bot in chat is medium-frequency (30min cap fires on genuinely
// distinct deactivations). Workspace-wide deactivation is rare and
// high-intent — every event is worth capturing.
//
// `getOrInitSlbActivatedAt` + `workspaceSlbAgeDaysFrom` stay exported
// because the `workspace_slb_age_days` analytics field is still
// dashboard-relevant — slicing reasons by workspace tenure — even
// though it no longer gates the modal.

import type { tHandoffReasonEntryPoint } from 'src/components/Chat/components/MessageBar/components/ToolActions/components/AutoRespondToggle/components/HandoffReasonModal/handoffReasonOptions.types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Per-entry-point cooldown windows. Zero means "never throttle".
// Sourced from RET-1069 V2 product spec (Fanny, 2026-05-14).
export const COOLDOWN_MS_BY_ENTRY_POINT: Record<
  tHandoffReasonEntryPoint,
  number
> = {
  tomar_control: 4 * HOUR_MS,
  apagar_bot_chat: 30 * MINUTE_MS,
  desactivar_workspace: 0,
};

// Per-(entry-point, user) last-asked timestamp. Separate key per surface
// so the 4h tomar_control cap doesn't suppress an apagar_bot_chat survey
// that happens to occur in the same window — they measure different
// operator intents and should throttle independently.
const lastAskedKey = (
  entryPoint: tHandoffReasonEntryPoint,
  userId: string,
): string =>
  `lead_agent_handoff_reason_last_asked_${entryPoint}_${userId}`;

// Workspace-wide first-seen marker, used only for analytics
// (`workspace_slb_age_days`) — no longer gates the modal under V2.
const firstSeenKey = (workspaceId: string): string =>
  `lead_agent_workspace_slb_first_seen_${workspaceId}`;

// Minimum storage surface — Pick of Storage so callers can pass `localStorage`
// or an in-memory test double without depending on the full Web Storage API.
type tStorageLike = Pick<Storage, 'getItem' | 'setItem'>;

type tShouldAskInput = {
  entryPoint: tHandoffReasonEntryPoint;
  userId: string;
  now: Date;
  storage?: tStorageLike;
};

const defaultStorage = (): tStorageLike | null =>
  typeof window === 'undefined' ? null : window.localStorage;

const readLastAsked = (
  entryPoint: tHandoffReasonEntryPoint,
  userId: string,
  storage: tStorageLike | null,
): number | null => {
  if (!storage) return null;
  const raw = storage.getItem(lastAskedKey(entryPoint, userId));
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};

// Returns true if the modal should be shown for this (entryPoint, user)
// right now. A surface with a 0-ms cooldown (desactivar_workspace) always
// returns true. Otherwise: true if the last-asked timestamp is missing
// or older than the cooldown window.
export function shouldAskForReason({
  entryPoint,
  userId,
  now,
  storage = defaultStorage() ?? undefined,
}: tShouldAskInput): boolean {
  const cooldown = COOLDOWN_MS_BY_ENTRY_POINT[entryPoint];
  if (cooldown === 0) return true;
  const lastAskedMs = readLastAsked(entryPoint, userId, storage ?? null);
  if (lastAskedMs === null) return true;
  return now.getTime() - lastAskedMs >= cooldown;
}

type tRecordInput = {
  entryPoint: tHandoffReasonEntryPoint;
  userId: string;
  now: Date;
  storage?: tStorageLike;
};

// Records that the modal was answered for this (entryPoint, user) at
// `now`. Call this on operator interaction (Confirm / Skip / Close),
// not when the modal opens — the chat-switch dismissal path opens then
// silently closes, and we don't want that to burn the cap.
export function recordReasonAsked({
  entryPoint,
  userId,
  now,
  storage = defaultStorage() ?? undefined,
}: tRecordInput): void {
  if (!storage) return;
  storage.setItem(lastAskedKey(entryPoint, userId), String(now.getTime()));
}

type tGetOrInitSlbActivatedAtInput = {
  workspaceId: string;
  now: Date;
  storage?: tStorageLike;
};

// Browser-local SLB activation marker. First observation per workspace
// is the activation date for `workspace_slb_age_days` analytics. The
// V1 14-day always-ask window is gone; this is now an analytics-only
// signal so dashboards can slice reasons by workspace tenure. A user
// clearing localStorage resets the marker — bounded by the per-entry-
// point cooldown which prevents fatigue regardless.
export function getOrInitSlbActivatedAt({
  workspaceId,
  now,
  storage = defaultStorage() ?? undefined,
}: tGetOrInitSlbActivatedAtInput): Date {
  if (!storage) return now;
  const raw = storage.getItem(firstSeenKey(workspaceId));
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  if (Number.isFinite(parsed)) return new Date(parsed);
  storage.setItem(firstSeenKey(workspaceId), String(now.getTime()));
  return now;
}

// Days between the workspace SLB activation marker and `now`, floored.
// Only used for the `workspace_slb_age_days` analytics payload field.
export function workspaceSlbAgeDaysFrom(
  slbActivatedAt: Date,
  now: Date,
): number {
  return Math.floor((now.getTime() - slbActivatedAt.getTime()) / DAY_MS);
}
